// 폰 제스처 (spec docs/superpowers/specs/2026-09-25-phone-landscape-design.md §E.2) — 전부 덧붙임이다:
// 같은 일을 하는 단추가 언제나 남아 있고, 여기서는 그 단추(또는 그 화면의 닫기 경로)를 부를 뿐이다.
//
//  1. 바텀 시트 끌어내려 닫기 — 공용 .m-sheet-panel 과 [data-m-drag="<닫기 단추>"] 패널
//  2. 드로어 — 열린 드로어를 왼쪽으로 밀어 닫기, 왼쪽 가장자리에서 밀어 열기
//  3. 기간 넘기기 — [data-m-swipe] 루트에서 가로로 밀면 [data-swipe-nav="prev|next"] 를 누른다
//  4. 당겨서 새로고침 — 설치한 앱(standalone)에서만
//
// 판정은 gesture-logic.js(순수 함수)가 한다. 세로 스크롤을 빼앗지 않도록 축이 정해지기 전에는
// 아무것도 하지 않고, 스크롤을 막아야 하는 곳(시트 패널·설치 앱의 문서)만 non-passive 로 듣는다.
import { isPhone } from './phone.js';
import { lockAxis, releaseDecision, resist, periodSwipe, pullState, velocityOf } from './gesture-logic.js';
import { closeAdminDrawer, openAdminDrawer } from './rail-tooltip.js';

const HEAD_ZONE = 48; // 시트 머리(손잡이·제목): 본문이 스크롤돼 있어도 여기서는 잡는다
const EDGE = 20; // 드로어를 여는 왼쪽 가장자리 폭
const SKIP = 'input, textarea, select, [contenteditable="true"], [data-no-swipe]';

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const visible = (el) => !!el && el.getClientRects().length > 0;

// 끝난 뒤 인라인 스타일을 지운다 — 트랜지션이 있으면 그 끝에서, 없으면 곧바로.
function settle(el, props, then) {
    let done = false;
    const finish = (e) => {
        if (done || (e && e.target !== el)) return; // 자식의 transitionend 가 거품으로 올라와도 끝이 아니다
        done = true;
        el.removeEventListener('transitionend', finish);
        props.forEach((p) => { el.style[p] = ''; });
        if (then) then();
    };
    if (reduced()) { finish(); return; }
    el.addEventListener('transitionend', finish);
    setTimeout(finish, 260);
}

// target 에서 stop 까지 올라가며 그 축으로 스크롤할 수 있는 상자가 있는가
function scrollableBetween(target, stop, axis) {
    for (let el = target; el && el !== stop && el !== document.body; el = el.parentElement) {
        const cs = getComputedStyle(el);
        if (axis === 'x' && /(auto|scroll)/.test(cs.overflowX) && el.scrollWidth > el.clientWidth + 1) return el;
        if (axis === 'y' && /(auto|scroll)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight + 1) return el;
    }
    return null;
}

// target 에서 위로 올라가며 왼쪽으로 스크롤할 수 있는(scrollLeft > 0) 가로 상자가 있는가
function canScrollLeft(target) {
    for (let el = target; el && el !== document.body; el = el.parentElement) {
        const cs = getComputedStyle(el);
        if (/(auto|scroll)/.test(cs.overflowX) && el.scrollWidth > el.clientWidth + 1 && el.scrollLeft > 0) return true;
    }
    return false;
}

// ── 1) 시트 ─────────────────────────────────────────────────────────────────
let closeSheetFn = null;

function sheetBackdrop(panel) {
    if (panel.matches('.m-sheet-panel')) return panel.parentElement.querySelector('.m-sheet-backdrop');
    const sel = panel.dataset.mDragBackdrop;
    return sel ? document.querySelector(sel) : null;
}

function closeDraggedSheet(panel) {
    if (panel.matches('.m-sheet-panel')) {
        const sheet = panel.closest('.m-sheet');
        if (sheet && closeSheetFn) closeSheetFn(sheet, true);
        return;
    }
    const btn = panel.dataset.mDrag ? document.querySelector(panel.dataset.mDrag) : null;
    if (btn) btn.click();
}

const CLOSE_GRACE = 160; // 닫기 경로가 확인 없이 비동기로 닫히는 시간(ms) — 이 안에 닫히면 되돌리지 않는다
// (지금의 닫기 경로 넷은 모두 같은 틱이나 마이크로태스크에 닫힌다 — fetch 를 기다리는 닫기가 생기면 이 값을 다시 본다)
const CLOSE_WAIT_MAX = 120000; // 확인 창이 끝내 사라지지 않으면 이만큼 뒤에 제자리로

function closeAfterDrag(panel, bd) {
    const clear = () => {
        panel.style.transform = '';
        panel.style.transition = '';
        if (bd) { bd.style.opacity = ''; bd.style.transition = ''; }
    };
    const springBack = () => {
        panel.style.transition = reduced() ? 'none' : 'transform .18s ease-out';
        panel.style.transform = '';
        if (bd) { bd.style.transition = reduced() ? 'none' : 'opacity .18s ease-out'; bd.style.opacity = ''; }
        settle(panel, ['transition'], () => { if (bd) bd.style.transition = ''; });
    };
    const started = performance.now();
    let since = started;
    const check = () => {
        if (!visible(panel)) { clear(); return; } // 닫혔다
        const asking = !!document.querySelector('.confirm-dialog-overlay');
        if (asking && performance.now() - started < CLOSE_WAIT_MAX) { since = performance.now(); requestAnimationFrame(check); return; } // 묻는 중
        if (performance.now() - since < CLOSE_GRACE) { requestAnimationFrame(check); return; }
        springBack(); // 머물기를 골랐다
    };
    // 닫기 경로가 던져도 패널이 화면 밖에 남지 않게 — 확인은 언제나 돈다
    try { closeDraggedSheet(panel); } finally { requestAnimationFrame(check); }
}

function bindSheetPanel(panel) {
    if (panel.__mDrag) return;
    panel.__mDrag = true;
    let t = null;
    panel.addEventListener('touchstart', (e) => {
        // 두 번째 손가락(또는 끝을 놓친 손가락): 하던 끌기는 버리고 제자리로 — 반쯤 내려간 채 남지 않게
        if (t) end(e, true);
        if (!isPhone() || e.touches.length !== 1 || e.target.closest(SKIP)) return;
        const p = e.touches[0];
        const rect = panel.getBoundingClientRect();
        const inHead = p.clientY - rect.top <= HEAD_ZONE;
        const scroller = scrollableBetween(e.target, panel.parentElement, 'y');
        if (!inHead && scroller && scroller.scrollTop > 0) return; // 본문이 스크롤돼 있으면 스크롤이다
        t = { x: p.clientX, y: p.clientY, axis: null, owned: false, samples: [{ t: e.timeStamp, v: p.clientY }], backdrop: sheetBackdrop(panel) };
    }, { passive: true });
    panel.addEventListener('touchmove', (e) => {
        if (!t) return;
        const p = e.touches[0];
        const dx = p.clientX - t.x;
        const dy = p.clientY - t.y;
        if (!t.axis) {
            t.axis = lockAxis(dx, dy);
            if (!t.axis) return;
            if (t.axis !== 'y' || dy < 0) { t = null; return; } // 가로·위로 = 스크롤이나 다른 일
            t.owned = true;
            panel.style.transition = 'none';
            if (t.backdrop) t.backdrop.style.transition = 'none';
        }
        if (!t.owned) return;
        if (e.cancelable) e.preventDefault();
        t.samples.push({ t: e.timeStamp, v: p.clientY });
        const off = Math.max(0, dy);
        panel.style.transform = `translateY(${off}px)`;
        if (t.backdrop) t.backdrop.style.opacity = String(Math.max(0.15, 1 - off / (panel.offsetHeight || 1)));
    }, { passive: false });
    function end(e, cancelled) {
        if (!t || !t.owned) { t = null; return; }
        const p = e.changedTouches && e.changedTouches[0];
        const dy = p ? p.clientY - t.y : 0;
        const commit = !cancelled && releaseDecision({ delta: dy, velocity: velocityOf(t.samples), size: panel.offsetHeight || 1 });
        const bd = t.backdrop;
        t = null;
        panel.style.transition = reduced() ? 'none' : 'transform .18s ease-out';
        if (bd) bd.style.transition = reduced() ? 'none' : 'opacity .18s ease-out';
        if (commit) {
            panel.style.transform = 'translateY(100%)';
            if (bd) bd.style.opacity = '0';
            // 화면 밖에 둔 채로 닫는다 — 닫기 경로가 확인(저장하지 않은 내용)을 띄우면 답이 날 때까지 그대로 두고,
            // 머물기를 고르면 그때 제자리로 올라온다(제자리로 튀었다가 확인이 뜨지 않게).
            settle(panel, ['transition'], () => closeAfterDrag(panel, bd));
        } else {
            panel.style.transform = '';
            if (bd) bd.style.opacity = '';
            settle(panel, ['transition'], () => { if (bd) bd.style.transition = ''; });
        }
    }
    panel.addEventListener('touchend', (e) => end(e, false));
    panel.addEventListener('touchcancel', (e) => end(e, true));
}

function bindSheets(root = document) {
    root.querySelectorAll('.m-sheet-panel, [data-m-drag]').forEach(bindSheetPanel);
}

// ── 2) 드로어 ───────────────────────────────────────────────────────────────
const drawer = () => document.getElementById('adminSidebar');
const drawerBackdrop = () => document.querySelector('.admin-sidebar-backdrop');
const drawerOpen = () => !!drawer()?.classList.contains('mobile-open');
const burgerVisible = () => visible(document.querySelector('.hamburger-menu'));
const anyOverlay = () => document.body.classList.contains('modal-scroll-lock')
    || document.documentElement.classList.contains('work-panel-open')
    || document.body.classList.contains('eval-issue-pop-open')
    || !!document.querySelector('.modal-overlay.show, .att-modal[style*="flex"], .apv-modal:not([hidden])');

let dt = null; // 드로어 추적

function drawerStart(e) {
    if (dt) drawerEnd(e, true); // 두 번째 손가락: 끌던 드로어는 원래 자리로
    if (!isPhone() || e.touches.length !== 1) return;
    const sb = drawer();
    if (!sb || !burgerVisible()) return;
    const p = e.touches[0];
    if (drawerOpen()) {
        if (!e.target.closest('#adminSidebar, .admin-sidebar-backdrop') || e.target.closest(SKIP)) return;
        dt = { mode: 'close', x: p.clientX, y: p.clientY, axis: null, samples: [{ t: e.timeStamp, v: p.clientX }] };
    } else if (p.clientX <= EDGE && !anyOverlay() && !e.target.closest(SKIP) && !canScrollLeft(e.target)) {
        // 왼쪽으로 되돌릴 수 있는 가로 띠(칩·탭) 위의 손가락은 그 띠의 스크롤이다
        dt = { mode: 'open', x: p.clientX, y: p.clientY, axis: null, samples: [{ t: e.timeStamp, v: p.clientX }] };
    }
}

function drawerMove(e) {
    if (!dt) return false;
    const p = e.touches[0];
    const dx = p.clientX - dt.x;
    const dy = p.clientY - dt.y;
    if (!dt.axis) {
        dt.axis = lockAxis(dx, dy);
        if (!dt.axis) return true;
        if (dt.axis !== 'x' || (dt.mode === 'close' ? dx > 0 : dx < 0)) { dt = null; return false; }
        const sb = drawer();
        sb.style.transition = 'none';
        const bd = drawerBackdrop();
        if (bd) bd.style.transition = 'none';
    }
    dt.samples.push({ t: e.timeStamp, v: p.clientX });
    const sb = drawer();
    const w = sb.offsetWidth || 1;
    if (dt.mode === 'close') {
        const off = Math.min(0, dx);
        sb.style.transform = `translateX(${off}px)`;
        const bd = drawerBackdrop();
        if (bd) bd.style.opacity = String(Math.max(0, 1 + off / w));
    } else {
        const off = Math.min(w, Math.max(0, dx));
        sb.style.transform = `translateX(${off - w}px)`;
    }
    return true;
}

function drawerEnd(e, cancelled) {
    if (!dt) return;
    const t = dt;
    dt = null;
    if (!t.axis) return; // 움직이지 않은 탭 — 배경의 onclick 이 알아서 닫는다
    const sb = drawer();
    const bd = drawerBackdrop();
    const p = e.changedTouches && e.changedTouches[0];
    const dx = p ? p.clientX - t.x : 0;
    const v = velocityOf(t.samples);
    const w = sb.offsetWidth || 1;
    sb.style.transition = reduced() ? 'none' : 'transform .2s ease-out';
    if (bd) bd.style.transition = '';
    const clear = () => { if (bd) bd.style.opacity = ''; };
    if (t.mode === 'close') {
        if (!cancelled && releaseDecision({ delta: -dx, velocity: -v, size: w })) {
            sb.style.transform = 'translateX(-100%)';
            settle(sb, ['transform', 'transition'], () => { closeAdminDrawer({ restoreFocus: true }); clear(); });
            // 클래스가 빠지기 전에 인라인 위치를 지워도 이미 화면 밖이라 튀지 않는다
        } else {
            sb.style.transform = '';
            clear();
            settle(sb, ['transition']);
        }
    } else if (!cancelled && releaseDecision({ delta: dx, velocity: v, size: w })) {
        sb.style.transform = 'translateX(0)';
        openAdminDrawer();
        settle(sb, ['transform', 'transition']);
    } else {
        sb.style.transform = 'translateX(-100%)';
        settle(sb, ['transform', 'transition']);
    }
}

// ── 3) 기간 넘기기 ──────────────────────────────────────────────────────────
let st = null; // 기간 스와이프 추적

// 루트 안의 보이고 켜진 단추가 먼저; 루트 안에 단추가 없거나 다 못 쓰면 화면의 단추(루트가 contained 면 거기서 멈춘다).
function navControl(root, dir) {
    const usable = (el) => visible(el) && !el.disabled && el.getAttribute('aria-disabled') !== 'true';
    const inside = Array.from(root.querySelectorAll(`[data-swipe-nav="${dir}"]`));
    const hit = inside.find(usable);
    if (hit) return hit;
    if (root.dataset.mSwipe === 'contained') return null;
    return Array.from(document.querySelectorAll(`[data-swipe-nav="${dir}"]`)).find((el) => !root.contains(el) && usable(el)) || null;
}

function periodStart(e) {
    if (st) periodEnd(e, true); // 두 번째 손가락: 따라오던 화면은 제자리로, 넘기지 않는다
    if (!isPhone() || e.touches.length !== 1) return;
    const root = e.target.closest('[data-m-swipe]');
    if (!root || e.target.closest(SKIP) || scrollableBetween(e.target, root, 'x')) return;
    const p = e.touches[0];
    st = { root, x: p.clientX, y: p.clientY, axis: null, moved: false };
}

function periodMove(e) {
    if (!st) return;
    const p = e.touches[0];
    const dx = p.clientX - st.x;
    const dy = p.clientY - st.y;
    if (!st.axis) {
        st.axis = lockAxis(dx, dy);
        if (!st.axis) return;
        if (st.axis !== 'x') { st = null; return; }
        if (!reduced()) st.root.style.transition = 'none';
    }
    if (!reduced()) {
        st.moved = true;
        st.root.style.transform = `translateX(${resist(dx, 56)}px)`;
    }
}

function periodEnd(e, cancelled) {
    if (!st) return;
    const t = st;
    st = null;
    if (t.moved) {
        t.root.style.transition = 'transform .18s ease-out';
        t.root.style.transform = '';
        settle(t.root, ['transition']);
    }
    if (cancelled) return;
    const p = e.changedTouches && e.changedTouches[0];
    if (!p) return;
    const dir = periodSwipe(p.clientX - t.x, p.clientY - t.y);
    if (!dir) return;
    const ctl = navControl(t.root, dir);
    if (ctl) ctl.click();
}

// ── 4) 당겨서 새로고침 (설치한 앱에서만) ────────────────────────────────────
const standalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
let pt = null;
let ptrEl = null;
const PTR_STUCK_MS = 4000; // 새로 고침이 이 안에 문서를 바꾸지 않으면 떠나기가 취소된 것으로 본다(느린 망의 새로 고침은 그 전에 바뀐다)
let ptrStuckTimer = 0;

function ptrIndicator() {
    if (ptrEl && ptrEl.isConnected) return ptrEl;
    ptrEl = document.createElement('div');
    ptrEl.id = 'mPtr';
    ptrEl.className = 'm-ptr';
    ptrEl.hidden = true;
    ptrEl.setAttribute('aria-hidden', 'true');
    ptrEl.innerHTML = '<span class="m-ptr-icon">↻</span>';
    document.body.appendChild(ptrEl);
    return ptrEl;
}

function atTop(target) {
    const se = document.scrollingElement || document.documentElement;
    if (se.scrollTop > 0) return false;
    // 손가락 아래의 세로 스크롤 상자가 맨 위가 아니면 그 상자의 스크롤이다
    for (let el = target; el && el !== document.body; el = el.parentElement) {
        const cs = getComputedStyle(el);
        if (/(auto|scroll)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight + 1 && el.scrollTop > 0) return false;
    }
    return true;
}

function ptrStart(e) {
    if (pt) ptrEnd(e, true); // 두 번째 손가락: 내려오던 표시는 거둔다
    if (ptrStuckTimer) { clearTimeout(ptrStuckTimer); ptrStuckTimer = 0; } // 머물기 뒤의 새 당김 — 앞 타이머가 새 표시를 걷지 않게
    if (!standalone() || !isPhone() || e.touches.length !== 1 || drawerOpen() || anyOverlay()) return;
    if (e.target.closest(SKIP) || !atTop(e.target)) return;
    const p = e.touches[0];
    pt = { x: p.clientX, y: p.clientY, axis: null, owned: false, ready: false };
}

function ptrMove(e) {
    if (!pt) return;
    const p = e.touches[0];
    const dx = p.clientX - pt.x;
    const dy = p.clientY - pt.y;
    if (!pt.axis) {
        pt.axis = lockAxis(dx, dy);
        if (!pt.axis) return;
        if (pt.axis !== 'y' || dy < 0) { pt = null; return; }
        pt.owned = true;
    }
    if (e.cancelable) e.preventDefault();
    const s = pullState(dy);
    pt.ready = s.ready;
    const el = ptrIndicator();
    el.hidden = false;
    el.dataset.ready = s.ready ? '1' : '';
    el.style.transform = `translate(-50%, ${s.offset}px) rotate(${Math.round(s.offset * 3)}deg)`;
}

function ptrEnd(e, cancelled) {
    if (!pt) return;
    const t = pt;
    pt = null;
    if (!t.owned) return;
    const el = ptrIndicator();
    if (t.ready && !cancelled) {
        el.classList.add('m-ptr-spin');
        window.location.reload();
        // 떠나기 확인에서 머물기를 고르면 문서가 그대로 남는다 — 그때는 돌던 표시를 거둔다
        clearTimeout(ptrStuckTimer);
        ptrStuckTimer = setTimeout(() => {
            ptrStuckTimer = 0;
            el.classList.remove('m-ptr-spin');
            el.style.transform = 'translate(-50%, 0)';
            el.hidden = true;
            el.dataset.ready = '';
        }, PTR_STUCK_MS);
        return;
    }
    el.style.transition = reduced() ? 'none' : 'transform .18s ease-out';
    el.style.transform = 'translate(-50%, 0)';
    settle(el, ['transition', 'transform'], () => { el.hidden = true; el.dataset.ready = ''; });
}

// ── 묶기 ────────────────────────────────────────────────────────────────────
let wired = false;
export function setupTouchGestures({ closeSheet } = {}) {
    closeSheetFn = closeSheet || closeSheetFn;
    bindSheets();
    if (wired) return;
    wired = true;
    // 새 화면(SPA 교체)이 가져온 패널도 묶는다 — 묶음 표시는 노드의 JS 속성이라 새 노드만 묶인다
    ['calendar:page-swapped', 'calendar:navigated', 'calendar:refreshed', 'calendar:root-added'].forEach((name) => {
        document.addEventListener(name, () => bindSheets());
    });
    document.addEventListener('touchstart', (e) => {
        // 한 손가락은 한 제스처만: 가장자리에서 시작한 손가락이 드로어를 열면서 달력을 넘기지 않게
        drawerStart(e);
        if (dt) { // 이 손가락은 드로어의 것이다 — 남은 다른 추적은 제자리로 돌려 놓고 끝낸다
            if (st) periodEnd(e, true);
            if (pt) ptrEnd(e, true);
            return;
        }
        periodStart(e);
        ptrStart(e);
    }, { passive: true });
    document.addEventListener('touchmove', (e) => {
        if (drawerMove(e)) return;
        periodMove(e);
    }, { passive: true });
    document.addEventListener('touchend', (e) => { drawerEnd(e, false); periodEnd(e, false); ptrEnd(e, false); }, { passive: true });
    document.addEventListener('touchcancel', (e) => { drawerEnd(e, true); periodEnd(e, true); ptrEnd(e, true); }, { passive: true });
    // 당겨서 새로고침만 문서 스크롤을 막아야 한다(끌어내린 만큼 페이지가 따라 내려오지 않게) —
    // 그래서 non-passive 문서 리스너는 설치한 앱에서만 단다: 브라우저 탭의 스크롤은 계속 비동기로 돈다.
    if (standalone()) document.addEventListener('touchmove', ptrMove, { passive: false });
    document.documentElement.dataset.mGestures = '1';
}
